/**
 * mm-funnel-track.js — temporary funnel-friction diagnostic (Jun 2026).
 *
 * Fires custom events to GA4 (gtag) + GTM (dataLayer) + Clarity for the
 * NON-PDP add-to-cart paths: quick-add on product cards, the cart drawer, and
 * cart errors. The PDP itself instruments pdp_atc_* inline in
 * sections/mm-product.liquid.
 *
 * Goal: tell "shoppers click but the add fails" (a real bug) from "fewer
 * shoppers click" (intent / trust / speed), after the Horizon
 * view_item -> add_to_cart rate dropped across every channel.
 *
 * All listeners are capture-phase + try/caught so a native component that calls
 * stopPropagation or throws can never break our measurement (or the store).
 */
(function () {
  'use strict';

  function mmTrack(name, params) {
    try {
      var p = params || {};
      if (typeof window.gtag === 'function') window.gtag('event', name, p);
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, p));
      }
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (e) {}
  }

  // Horizon tags every quick-add cart mutation with this source id (see
  // assets/cart-drawer-extras.js: QUICK_ADD_SOURCE_ID).
  var QUICK_ADD_SOURCE = 'cart-drawer-quick-add';
  // Quick-add lives in <quick-add-component> on cards and opens <quick-add-dialog>
  // / .quick-add-modal for multi-variant products.
  var QA_CTX = 'quick-add-component, quick-add-dialog, .quick-add-modal';

  // --- quick_add_open: shopper engages a quick-add button on a product card ---
  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (t && t.closest && t.closest('quick-add-component') && t.closest('button')) {
        mmTrack('quick_add_open', {});
      }
    } catch (err) {}
  }, true);

  // --- quick_add_submit: quick-add form submitted (direct add or via modal) ---
  document.addEventListener('submit', function (e) {
    try {
      if (e.target && e.target.closest && e.target.closest(QA_CTX)) {
        mmTrack('quick_add_submit', {});
      }
    } catch (err) {}
  }, true);

  // --- quick_add_variant_selected: a variant chosen inside the quick-add modal ---
  document.addEventListener('variant:selected', function (e) {
    try {
      var t = e.target;
      if (t && t.closest && t.closest(QA_CTX)) {
        mmTrack('quick_add_variant_selected', {});
      }
    } catch (err) {}
  }, true);

  // --- success / error, distinguished by Horizon's cart source id ---
  document.addEventListener('cart:update', function (e) {
    try {
      var d = e.detail || {};
      if (d.sourceId === QUICK_ADD_SOURCE) {
        mmTrack('quick_add_success', { variant_id: (d.data && d.data.variantId) || null });
      }
    } catch (err) {}
  }, true);

  document.addEventListener('cart:error', function (e) {
    try {
      var d = e.detail || {};
      var msg = (d.data && d.data.message) || '';
      mmTrack('cart_add_error', { source: d.sourceId || 'unknown', message: String(msg).slice(0, 120) });
      if (d.sourceId === QUICK_ADD_SOURCE) {
        mmTrack('quick_add_error', { message: String(msg).slice(0, 120) });
      }
    } catch (err) {}
  }, true);

  // --- cart_drawer_open: any open of the cart drawer, from any source ---
  // dialog:open does NOT bubble, but a capture-phase listener still receives it
  // as the event descends document -> target. Filter to the cart drawer so the
  // search modal and quick-add dialog (same base DialogComponent) don't count.
  document.addEventListener('dialog:open', function (e) {
    try {
      var t = e.target;
      if (t && ((t.matches && t.matches('cart-drawer-component')) || (t.closest && t.closest('cart-drawer-component')))) {
        mmTrack('cart_drawer_open', {});
      }
    } catch (err) {}
  }, true);

  function cartDrawerCrossSellContext(card) {
    var position = null;
    try {
      var list = card && card.parentElement;
      if (list) {
        var cards = Array.prototype.slice.call(list.querySelectorAll('.cart-drawer-recommendations__card'));
        var index = cards.indexOf(card);
        if (index >= 0) position = index + 1;
      }
    } catch (err) {}

    return {
      source: 'cart_drawer_recommendations',
      target_product_title: (card && card.dataset && card.dataset.productTitle) || null,
      position: position
    };
  }

  // --- Cross-sell interaction: PDP uses the same event names inline. ---
  // The source parameter keeps "El match perfecto" and cart-drawer results
  // separable in one GA4 exploration.
  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;

      var directAdd = t.closest('[data-cart-drawer-add]');
      if (directAdd) {
        var directCard = directAdd.closest('.cart-drawer-recommendations__card');
        mmTrack('cross_sell_atc_click', Object.assign(cartDrawerCrossSellContext(directCard), {
          cross_sell_action: 'direct_add',
          variant_id: directAdd.dataset.variantId || null
        }));
        return;
      }

      var optionsButton = t.closest('[data-cd-sheet-open]');
      if (optionsButton) {
        var optionsCard = optionsButton.closest('.cart-drawer-recommendations__card');
        mmTrack('cross_sell_card_click', Object.assign(cartDrawerCrossSellContext(optionsCard), {
          cross_sell_action: 'open_options'
        }));
        return;
      }

      var sheetAdd = t.closest('[data-cd-sheet-atc]');
      if (sheetAdd) {
        var sheet = sheetAdd.closest('[data-cd-sheet]');
        var sheetTitle = sheet && sheet.querySelector('[data-cd-sheet-title]');
        mmTrack('cross_sell_atc_click', {
          source: 'cart_drawer_recommendations',
          target_product_title: sheetTitle ? String(sheetTitle.textContent || '').trim() : null,
          cross_sell_action: 'sheet_add'
        });
        return;
      }

      var productLink = t.closest('.cart-drawer-recommendations__image-wrap, .cart-drawer-recommendations__title-link');
      if (productLink) {
        var linkCard = productLink.closest('.cart-drawer-recommendations__card');
        mmTrack('cross_sell_card_click', Object.assign(cartDrawerCrossSellContext(linkCard), {
          cross_sell_action: 'view_product'
        }));
        return;
      }

      var checkoutButton = t.closest('.cart__checkout-button');
      if (checkoutButton && checkoutButton.closest('cart-drawer-component')) {
        mmTrack('cart_drawer_checkout_click', { source: 'cart_drawer_sticky_checkout' });
      }
    } catch (err) {}
  }, true);
})();
